import { ReactNode } from 'react';
import { X } from 'lucide-react';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onFilter: () => void;
  onClear: () => void;
  primaryButtonLabel?: string;
  /** data-testid for the primary action button. Distinguishes reused panels (e.g. filter vs invite). */
  primaryTestId?: string;
  secondaryButtonLabel?: string;
  clearButtonLabel?: string;
  showClearButton?: boolean;
  isPrimaryLoading?: boolean;
  isPrimaryDisabled?: boolean;
  children?: ReactNode;
}

export const FilterPanel = ({
  isOpen,
  onClose,
  onFilter,
  onClear,
  primaryButtonLabel = 'Filter',
  primaryTestId = 'filter-apply',
  secondaryButtonLabel = 'Cancel',
  clearButtonLabel = 'Clear',
  showClearButton = true,
  isPrimaryLoading = false,
  isPrimaryDisabled = false,
  children,
}: FilterPanelProps): JSX.Element => {
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 bg-white z-50 shadow-lg transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '440px',
          height: '100vh',
          paddingLeft: '40px'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close panel"
          className="absolute top-6 right-6 hover:bg-neutral-100 transition-colors z-10"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={20} color="#344054" strokeWidth={1.67} aria-hidden />
        </button>
        
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto" style={{ paddingRight: '24px', paddingBottom: '24px' }}>
          {children}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex justify-end gap-3 pt-6 pb-6 flex-shrink-0" style={{
          borderTop: '1px solid #E5E7EB',
          paddingRight: '24px'
        }}>
          {showClearButton && (
            <button
              type="button"
              onClick={onClear}
              data-testid="filter-clear"
              style={{
                height: '40px',
                padding: '0 16px',
                borderRadius: '6px',
                border: '1px solid #D0D5DD',
                backgroundColor: 'white',
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#344054'
              }}
              className="hover:bg-neutral-50 transition-colors"
            >
              {clearButtonLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="filter-cancel"
            style={{
              height: '40px',
              padding: '0 16px',
              borderRadius: '6px',
              border: '1px solid #D0D5DD',
              backgroundColor: 'white',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              fontWeight: 400,
              color: '#344054'
            }}
            className="hover:bg-neutral-50 transition-colors"
          >
            {secondaryButtonLabel}
          </button>
          <button
            type="button"
            onClick={onFilter}
            data-testid={primaryTestId}
            disabled={isPrimaryLoading || isPrimaryDisabled}
            style={{
              height: '40px',
              padding: '0 24px',
              borderRadius: '6px',
              backgroundColor: '#404040',
              border: 'none',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              fontWeight: 400,
              color: '#EAECF0'
            }}
            className="hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPrimaryLoading ? '...' : primaryButtonLabel}
          </button>
        </div>
      </div>
    </>
  );
};
